import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditIcon from "@mui/icons-material/Edit";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { showToast } from "services/utils/Status";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import { useFetch } from "services/hooks/useFetch";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import RequestStatusTimeline from "components/DesignSystem/RequestStatusTimeline";
import ApprovalEditAuditTrail from "components/DesignSystem/ApprovalEditAuditTrail";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { RequestCancelButton } from "components/DesignSystem/RequestCancelButton";
import {
  detailAccentPalette,
  detailCardSx,
  detailSectionLabelSx,
  RequestDetailField,
} from "components/DesignSystem/RequestDetailField";
import {
  GlobalConfirmDialog,
  globalTextFieldSx as advanceSalaryTextFieldSx,
} from "components/DesignSystem/sharedUiPatterns";
import { approvePanelUi, useGoToApprovalCenter } from "../approveUi";
import {
  checkFirstLevelApproverPermission,
  checkSecondLevelApproverPermission,
} from "services/utils/HelperFunction";
import { advanceSalaryStatusVisual } from "./AdvanceSalaryStatusCards";
import AdvanceSalaryPanel from "pages/MyZone/MyZoneApply/AdvanceSalary/AdvanceSalaryFormPanel";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { minimum3Characters } from "services/constants/ErrorMessages";
import {
  designColors,
  designRadii,
  designShell,
} from "services/constants/designTokens";

const detailAccent = {
  amount: detailAccentPalette.green,
  mode: detailAccentPalette.indigo,
  installments: detailAccentPalette.blue,
  date: detailAccentPalette.purple,
  reason: detailAccentPalette.orange,
  rejectReason: detailAccentPalette.pink,
};

const isDetailedStatus = (status) =>
  ["Approved", "Active", "Partially-Paid", "Pre-Closed", "Closed"].includes(
    status,
  );

const formatAmount = (value) => {
  if (value == null || value === "") return "-";
  return `Rs. ${value}`;
};

const formatDisplayDate = (value) => {
  if (!value) return "-";
  const parsed = dayjs(value, ["YYYY-MM-DD", "DD-MM-YYYY", dayjs.ISO_8601], true);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : value;
};

const formatDisplayDateTime = (value) => {
  if (!value) return "-";
  const parsed = dayjs(
    value,
    [
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DD HH:mm",
      "DD-MM-YYYY, HH:mm",
      "DD-MM-YYYY HH:mm",
      "DD-MM-YYYY",
      "YYYY-MM-DD",
      dayjs.ISO_8601,
    ],
    true,
  );
  if (!parsed.isValid()) return value;
  const hasTime = /\d{1,2}:\d{2}/.test(String(value));
  return parsed.format(hasTime ? "DD MMM YYYY, hh:mm A" : "DD MMM YYYY");
};

const SummaryCard = ({ title, amount, icon: Icon, accent, tint }) => (
  <Mui.Paper
    variant="outlined"
    sx={{
      p: 1.5,
      borderRadius: designRadii.card,
      borderColor: designColors.slate200,
      flex: 1,
      minWidth: 0,
    }}
  >
    <Mui.Stack direction="row" spacing={1} alignItems="center" mb={1}>
      <Mui.Box
        sx={{
          p: 0.5,
          borderRadius: 1,
          bgcolor: tint,
          color: accent,
          display: "flex",
        }}
      >
        <Icon fontSize="small" />
      </Mui.Box>
      <Mui.Typography
        sx={{ fontSize: 12, color: designColors.slate500, fontWeight: 700 }}
      >
        {title}
      </Mui.Typography>
    </Mui.Stack>
    <Mui.Typography
      sx={{ fontSize: 18, fontWeight: 700, color: designColors.slate900 }}
    >
      {formatAmount(amount)}
    </Mui.Typography>
  </Mui.Paper>
);

export const AdvanceSalaryDetailPanel = ({
  open,
  onClose,
  advanceSalary,
  checkAccess,
  setReCallApi,
  expanded = false,
  onToggleExpand,
}) => {
  const goToApprovalCenter = useGoToApprovalCenter();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectedReason, setRejectedReason] = useState("");
  const [rejectedReasonError, setRejectedReasonError] = useState("");
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  // Reuses the same dedicated cancel endpoint MyZone's own Advance Salary
  // screen uses (AdvanceSalaryFormPanel.js's handleCancelRequest, PUT
  // advance/request/{id}/cancel/) — that's normally only reachable while
  // the request is Pending; here it's opened up to an Approved-but-not-yet-
  // disbursed request too, per user request, same as Comp Off.
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonError, setCancelReasonError] = useState("");
  const [detailedData, setDetailedData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [editPanelOpen, setEditPanelOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRepaymentRow, setSelectedRepaymentRow] = useState(null);
  const [skipModalOpen, setSkipModalOpen] = useState(false);
  const [skipPayload, setSkipPayload] = useState({
    skip_payrun_id: "",
    reason: "",
  });
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewPayload, setReviewPayload] = useState({
    action: "",
    review_remarks: "",
  });
  const [expandedRow, setExpandedRow] = useState(null);
  const [skipLoading, setSkipLoading] = useState(false);
  const { domain, userDetails, topbarCompanyId, userScreenAccess } =
    useSelector((state) => state.tokenReducer);
  // See Leave/LeaveDetailPanel.js — Approve/Reject/Cancel used to be gated
  // by plain editor permission (checkAccess). Now requires real approver
  // permission (either level counts).
  const hasApproverAccess =
    checkFirstLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Advance Salary",
    ) ||
    checkSecondLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Advance Salary",
    );

  const status = advanceSalary?.status;
  const isPending = status === "Pending";
  const isApproved = status === "Approved";
  const isDetailedView = isDetailedStatus(status);
  // Approved-but-not-yet-disbursed requests have no repayment activity yet,
  // so Skip/Review Skip on the schedule doesn't apply until it's disbursed.
  const showRepaymentActions = isDetailedView && !isApproved;
  const employeeName = advanceSalary?.user_name || "N/A";
  const employeeId = advanceSalary?.employee_id || "-";
  const department = advanceSalary?.department || advanceSalary?.department_name || "-";
  const designation =
    advanceSalary?.designation || advanceSalary?.designation_name || "-";
  const isBusinessAdvance = advanceSalary?.advance_type === "Business";

  const fetchDetails = async () => {
    if (!advanceSalary?.id) return;
    setLoadingDetails(true);
    const res = await customGetAPI(`${domain}advance/request/${advanceSalary.id}/?company_id=${topbarCompanyId}`);
    const extractedData = res?.responseDetails?.data || res?.responseDetails || null;
    setDetailedData(extractedData);
    setLoadingDetails(false);
  };

  useEffect(() => {
    setRejectDialogOpen(false);
    setRejectedReason("");
    setRejectedReasonError("");
    setCancelDialogOpen(false);
    setCancelReason("");
    setCancelReasonError("");
    setDetailedData(null);
    setExpandedRow(null);
    setAnchorEl(null);
    setSelectedRepaymentRow(null);
    if (isDetailedView) {
      fetchDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advanceSalary?.id]);

  const displayData = useMemo(
    () => detailedData || advanceSalary || {},
    [detailedData, advanceSalary],
  );
  // The advance is fully done once it is Closed/Pre-Closed, or once every
  // instalment on its repayment schedule has been paid (the status column can
  // lag behind the schedule until payroll closes it out).
  const repaymentSchedule = displayData?.repayment_schedule;
  const isFullyRepaid =
    isDetailedView &&
    Array.isArray(repaymentSchedule) &&
    repaymentSchedule.length > 0 &&
    repaymentSchedule.every((row) => row.status === "Paid");
  const isAdvanceCompleted =
    status === "Closed" || status === "Pre-Closed" || isFullyRepaid;
  const timelineStatus =
    status === "Rejected"
      ? "Rejected"
      : status === "Closed"
        ? "Approved"
        : isDetailedView
          ? "Approved"
          : status;
  const rejectionReason =
    displayData?.rejection_reason || advanceSalary?.rejection_reason || "";
  const statusFlowData = useMemo(
    () => ({
      ...displayData,
      status: timelineStatus,
      applied_by: displayData?.applied_by || displayData?.user_name,
      applied_on: formatDisplayDateTime(
        displayData?.applied_on ||
          displayData?.applied_at ||
          displayData?.created_at ||
          displayData?.request_date,
      ),
      applied_reason: displayData?.reason || "-",
      // displayData?.approver_name is the assigned approver, stamped at
      // apply time before anyone has acted -- it is not proof of approval
      // (or rejection). The backend's approved_by already recovers the
      // real approver from the audit log when a since-cancelled advance's
      // own approver column has been overwritten by the canceller (see
      // test_advance_cancel_display_fields.py) -- preferring approver_name
      // here would silently undo that fix.
      approved_by: displayData?.approved_by,
      approved_at: formatDisplayDateTime(displayData?.approved_at),
      rejected_by: displayData?.rejected_by,
      rejected_at: formatDisplayDateTime(displayData?.rejected_at),
      rejected_reason: rejectionReason || undefined,
      // cancellation_reason is a genuinely separate column from
      // rejection_reason (AdvanceService.cancel) but wasn't previously
      // exposed by the serializer at all, so a cancelled advance never
      // showed why -- now that the backend returns it, surface it under
      // the key RequestStatusTimeline's Cancelled flow reads.
      cancelled_reason: displayData?.cancellation_reason || undefined,
    }),
    [displayData, rejectionReason, timelineStatus],
  );
  const payrunUrl = topbarCompanyId
    ? `${domain}payroll/operations/payrollprocessing/payrollruncycle/payschedules/?status=run&company_id=${topbarCompanyId}`
    : "";
  const { result: payruns = [] } = useFetch(payrunUrl);

  if (!advanceSalary) return null;

  const handleCloseAction = () => {
    onClose?.();
  };

  const handleEditClose = () => {
    setEditPanelOpen(false);
    fetchDetails();
    setReCallApi?.(true);
  };

  const handleQuickApprove = async () => {
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to approve this request",
      );
      return;
    }
    setApproving(true);
    const payload = isBusinessAdvance
      ? {
          approver_id: userDetails?.id,
          approved_amount: advanceSalary.amount,
          approver_comments: "Quick approved from view mode.",
        }
      : {
          approver_id: userDetails?.id,
          approved_amount: advanceSalary.amount,
          repayment_mode: advanceSalary.repayment_mode,
          approved_installments:
            advanceSalary.no_of_installments ||
            advanceSalary.number_of_installments ||
            1,
          approved_repayment_date:
            advanceSalary.repayment_date || advanceSalary.repayment_start_date
              ? dayjs(
                  advanceSalary.repayment_date ||
                    advanceSalary.repayment_start_date,
                  "DD-MM-YYYY",
                ).format("YYYY-MM-DD")
              : "",
          approver_comments: "Quick approved from view mode.",
          approved_repayment_frequency:
            advanceSalary.repayment_frequency || "Monthly",
        };
    const res = await customAPI(
      `${domain}advance/request/${advanceSalary.id}/approve/?company_id=${topbarCompanyId}`,
      "PUT",
      payload,
    );
    if (res?.ok) {
      showToast("success", "Advance request approved.");
      setReCallApi?.(true);
      onClose?.();
    } else {
      showToast("error", res?.message || "Failed to approve request.");
    }
    setApproving(false);
  };

  const submitReject = async () => {
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to reject this request",
      );
      return;
    }
    setRejecting(true);
    const payload = {
      approver_id: userDetails?.id,
      approver_comments: "",
      rejection_reason: rejectedReason,
    };
    const res = await customAPI(
      `${domain}advance/request/${advanceSalary.id}/reject/?company_id=${topbarCompanyId}`,
      "PUT",
      payload,
    );
    if (res?.ok) {
      showToast("success", "Advance request rejected.");
      setReCallApi?.(true);
      setRejectDialogOpen(false);
      onClose?.();
    } else {
      showToast("error", res?.message || "Failed to reject request.");
    }
    setRejecting(false);
  };

  const closeRejectDialog = () => {
    setRejectDialogOpen(false);
    setRejectedReason("");
    setRejectedReasonError("");
  };

  const submitCancel = async () => {
    const trimmed = cancelReason.trim();
    if (!trimmed) {
      setCancelReasonError("Reason for cancellation is required");
      return;
    }
    if (trimmed.length < 3) {
      setCancelReasonError(minimum3Characters);
      return;
    }
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to cancel this request",
      );
      return;
    }
    setCancelling(true);
    const res = await customAPI(
      `${domain}advance/request/${advanceSalary.id}/cancel/?company_id=${topbarCompanyId}`,
      "PUT",
      { reason: trimmed },
    );
    setCancelling(false);
    if (res?.ok) {
      showToast(
        "success",
        res?.responseDetails?.message || "Advance request cancelled.",
      );
      setReCallApi?.(true);
      setCancelDialogOpen(false);
      onClose?.();
    } else {
      showToast("error", res?.message || "Failed to cancel request.");
    }
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelReason("");
    setCancelReasonError("");
  };

  const validateRejectedReason = () => {
    const reason = rejectedReason.trim();
    if (!reason) return "Reject reason is required";
    if (reason.length < 3) return minimum3Characters;
    if (reason.length > 255) return "Maximum length is 255 characters";
    if (!atleastOneCharacterWithSpace.test(reason)) {
      return "Atleast one alphabet required";
    }
    return "";
  };

  const handleMenuClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRepaymentRow(row);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const openSkipModal = () => {
    setSkipPayload({ skip_payrun_id: "", reason: "" });
    setSkipModalOpen(true);
    handleMenuClose();
  };

  const openReviewModal = () => {
    setReviewPayload({ action: "", review_remarks: "" });
    setReviewModalOpen(true);
    handleMenuClose();
  };

  const handleSkipSubmit = async () => {
    if (!skipPayload.skip_payrun_id || !skipPayload.reason) {
      showToast("error", "Please provide a reason and select a payrun.");
      return;
    }
    setSkipLoading(true);
    const payload = {
      user_id: userDetails.id,
      skip_payrun_id: skipPayload.skip_payrun_id,
      reason: skipPayload.reason,
    };
    const res = await customAPI(
      `${domain}advance/repayment/${selectedRepaymentRow.id}/skip/?company_id=${topbarCompanyId}`,
      "POST",
      payload,
    );
    if (res?.ok) {
      showToast("success", "Skip requested.");
      setSkipModalOpen(false);
      fetchDetails();
    } else {
      showToast("error", res?.message || "Failed to submit skip request.");
    }
    setSkipLoading(false);
  };

  const handleReviewSubmit = async (actionType) => {
    if (actionType === "reject" && !reviewPayload.review_remarks) {
      showToast("error", "Please provide remarks for rejection.");
      return;
    }
    setSkipLoading(true);
    const skipId =
      selectedRepaymentRow?.skip_request_id ||
      selectedRepaymentRow?.skip_id ||
      selectedRepaymentRow?.id;
    const payload = {
      user_id: userDetails.id,
      action: actionType,
      review_remarks:
        reviewPayload.review_remarks || `${actionType} via panel.`,
    };
    const res = await customAPI(
      `${domain}advance/skip/${skipId}/review/?company_id=${topbarCompanyId}`,
      "POST",
      payload,
    );
    if (res?.ok) {
      showToast("success", `Skip request ${actionType}d.`);
      setReviewModalOpen(false);
      fetchDetails();
    } else {
      showToast("error", res?.message || `Failed to ${actionType} request.`);
    }
    setSkipLoading(false);
  };

  // Reject/Approve/Cancel below used to always render, only disabled (via
  // AccessGatedAction) when hasApproverAccess was false — leaving a
  // bordered footer strip with unusable buttons for an editor-only viewer.
  // Each branch now resolves to real content or a genuine `null` (same fix
  // as OverTime/DetailPanel.js) so GlobalSidePanel's own footer border only
  // draws when there's actually something to show.
  const footerContent = isPending ? (
    hasApproverAccess ? (
      <Mui.Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        sx={{ width: "100%" }}
      >
        <Mui.Button
          variant="outlined"
          startIcon={<HighlightOffIcon sx={{ fontSize: 17 }} />}
          onClick={() => setRejectDialogOpen(true)}
          sx={{
            ...designShell.panelActionButton,
            flex: 1,
            minWidth: 0,
            minHeight: 42,
            color: "#E11D48",
            borderColor: "#FDA4AF",
            backgroundColor: designColors.white,
            "&:hover": {
              color: "#BE123C",
              borderColor: "#FB7185",
              backgroundColor: "#FFF1F3",
            },
          }}
        >
          Reject
        </Mui.Button>
        <PrimaryButton
          startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 17 }} />}
          loading={approving}
          onClick={handleQuickApprove}
          sx={{ flex: 1, minWidth: 0, minHeight: 42 }}
        >
          Approve
        </PrimaryButton>
      </Mui.Stack>
    ) : null
  ) : isApproved ? (
    hasApproverAccess ? (
      <Mui.Stack spacing={1.5} sx={{ width: "100%" }}>
        <Mui.Typography
          sx={{ ...approvePanelUi.panelMutedText, textAlign: "center" }}
        >
          This request has already been approved.
        </Mui.Typography>
        <RequestCancelButton onClick={() => setCancelDialogOpen(true)}>
          Cancel Advance Request
        </RequestCancelButton>
      </Mui.Stack>
    ) : null
  ) : null;

  const headerActions =
    isPending && hasApproverAccess ? (
      <Mui.Tooltip title="Edit request" arrow>
        <Mui.IconButton
          size="small"
          onClick={() => setEditPanelOpen(true)}
          sx={{ color: designColors.slate700 }}
        >
          <EditIcon fontSize="small" />
        </Mui.IconButton>
      </Mui.Tooltip>
    ) : null;

  const rejectDialog = (
    <GlobalConfirmDialog
      open={rejectDialogOpen}
      title="Reject Advance Salary Request"
      onCancel={closeRejectDialog}
      onConfirm={() => {
        const error = validateRejectedReason();
        if (error) {
          setRejectedReasonError(error);
          return;
        }
        submitReject();
      }}
      confirmLabel="Reject"
      cancelLabel="Cancel"
      loading={rejecting}
      danger
      reasonValue={rejectedReason}
      onReasonChange={(value) => {
        setRejectedReason(value);
        if (rejectedReasonError) setRejectedReasonError("");
      }}
      reasonError={rejectedReasonError}
      reasonPlaceholder="Provide a reason for rejection"
      reasonMinRows={2}
      maxWidth="sm"
    >
      <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
        Enter the reason for rejecting this request.
      </Mui.Typography>
    </GlobalConfirmDialog>
  );

  const cancelDialog = (
    <GlobalConfirmDialog
      open={cancelDialogOpen}
      title="Cancel Advance Salary Request"
      onCancel={closeCancelDialog}
      onConfirm={submitCancel}
      confirmLabel="Continue"
      cancelLabel="Keep"
      loading={cancelling}
      loadingLabel="Cancelling..."
      danger
      reasonValue={cancelReason}
      onReasonChange={(value) => {
        setCancelReason(value);
        if (cancelReasonError) setCancelReasonError("");
      }}
      reasonError={cancelReasonError}
      reasonPlaceholder="Provide a reason for cancelling this approved advance request"
      reasonMinRows={2}
      maxWidth="sm"
    >
      <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
        This advance salary request has already been approved. Enter a
        reason to cancel it.
      </Mui.Typography>
    </GlobalConfirmDialog>
  );

  const skipDialog = (
    <Mui.Dialog
      open={skipModalOpen}
      onClose={() => setSkipModalOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: designRadii.card } }}
    >
      <Mui.DialogTitle
        sx={{ fontSize: 18, fontWeight: 700, color: designColors.slate900 }}
      >
        Skip Advance Repayment
      </Mui.DialogTitle>
      <Mui.DialogContent dividers>
        <Mui.Box sx={{ mb: 3 }}>
          <Mui.Typography sx={approvePanelUi.fieldLabel}>Skip Payrun</Mui.Typography>
          <Mui.FormControl fullWidth size="small">
            <Mui.Select
              value={skipPayload.skip_payrun_id}
              onChange={(e) =>
                setSkipPayload({
                  ...skipPayload,
                  skip_payrun_id: e.target.value,
                })
              }
              displayEmpty
            >
              <Mui.MenuItem value="" disabled>
                Select Payrun
              </Mui.MenuItem>
              {payruns.map((payrun) => (
                <Mui.MenuItem key={payrun.id} value={payrun.id}>
                  {payrun.name || payrun.label || `Payrun ${payrun.id}`}
                </Mui.MenuItem>
              ))}
            </Mui.Select>
          </Mui.FormControl>
        </Mui.Box>
        <Mui.Box>
          <Mui.Typography sx={approvePanelUi.fieldLabel}>Reason</Mui.Typography>
          <Mui.TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Enter Reason"
            value={skipPayload.reason}
            onChange={(e) =>
              setSkipPayload({ ...skipPayload, reason: e.target.value })
            }
            sx={advanceSalaryTextFieldSx}
          />
        </Mui.Box>
      </Mui.DialogContent>
      <Mui.DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <SecondaryButton onClick={() => setSkipModalOpen(false)}>
          Cancel
        </SecondaryButton>
        <PrimaryButton loading={skipLoading} onClick={handleSkipSubmit}>
          Skip
        </PrimaryButton>
      </Mui.DialogActions>
    </Mui.Dialog>
  );

  const reviewDialog = (
    <Mui.Dialog
      open={reviewModalOpen}
      onClose={() => setReviewModalOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: designRadii.card } }}
    >
      <Mui.DialogTitle
        sx={{ fontSize: 18, fontWeight: 700, color: designColors.slate900 }}
      >
        Review Skip Request
      </Mui.DialogTitle>
      <Mui.DialogContent dividers>
        <Mui.Typography sx={approvePanelUi.fieldLabel}>Remarks</Mui.Typography>
        <Mui.TextField
          fullWidth
          multiline
          rows={4}
          placeholder="Enter remarks..."
          value={reviewPayload.review_remarks}
          onChange={(e) =>
            setReviewPayload({
              ...reviewPayload,
              review_remarks: e.target.value,
            })
          }
          sx={advanceSalaryTextFieldSx}
        />
      </Mui.DialogContent>
      <Mui.DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Mui.Button
          variant="outlined"
          color="error"
          disabled={skipLoading}
          onClick={() => handleReviewSubmit("reject")}
        >
          Reject
        </Mui.Button>
        <PrimaryButton
          loading={skipLoading}
          onClick={() => handleReviewSubmit("approve")}
        >
          Approve
        </PrimaryButton>
      </Mui.DialogActions>
    </Mui.Dialog>
  );

  return (
    <>
      <GlobalSidePanel
        open={open}
        onClose={handleCloseAction}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        title="Advance Salary Request"
        subtitle="Request details and status"
        headerActions={headerActions}
        headerSx={{ px: 2.5, pt: 2.5, pb: 1.5 }}
        PaperProps={{
          sx: {
            backgroundColor: designColors.white,
            maxWidth: "92vw",
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
            overflow: "hidden",
          },
        }}
        breadcrumb={[
          { label: "Approval Center", onClick: goToApprovalCenter },
          { label: "Advance Salary", onClick: handleCloseAction },
          employeeName || "Advance Salary Request",
        ]}
        footer={footerContent}
      >
        <Mui.Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <RequestDetailHeader
            userDetails={{
              image_url: advanceSalary?.image_url,
              employee_id: employeeId,
              user_name: employeeName,
              department_name: department,
              designation,
            }}
            status={status}
            colorCode={advanceSalary?.color_code || advanceSalaryStatusVisual(status).accent}
          />

          {loadingDetails ? (
            <Mui.Box
              sx={{
                minHeight: 220,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Mui.CircularProgress size={28} />
            </Mui.Box>
          ) : (
            <>
              {isDetailedView ? (
                <Mui.Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                  <SummaryCard
                    title="Advance Amount"
                    amount={displayData.approved_amount || displayData.amount || "-"}
                    icon={ShoppingBagOutlinedIcon}
                    accent="#2ecc71"
                    tint="#eafaf1"
                  />
                  <SummaryCard
                    title="Paid Amount"
                    amount={displayData.paid_amount || "0.00"}
                    icon={AccountBalanceWalletOutlinedIcon}
                    accent="#e91e63"
                    tint="#fce4ec"
                  />
                  <SummaryCard
                    title="Balance"
                    amount={displayData.balance_amount || "-"}
                    icon={CardGiftcardOutlinedIcon}
                    accent="#3f51b5"
                    tint="#e8eaf6"
                  />
                </Mui.Stack>
              ) : null}

              <Mui.Box sx={detailCardSx}>
                <Mui.Typography sx={{ ...detailSectionLabelSx, mb: 1.75 }}>
                  Request Details
                </Mui.Typography>
                <RequestDetailField
                  icon={CurrencyRupeeIcon}
                  label={isDetailedView ? "Approved Amount" : "Requested Amount"}
                  value={formatAmount(
                    isDetailedView
                      ? displayData.approved_amount ||
                          displayData.effective_amount ||
                          displayData.amount
                      : displayData.amount,
                  )}
                  accent={detailAccent.amount}
                />
                <RequestDetailField
                  icon={AutorenewIcon}
                  label="Repayment Mode"
                  value={displayData.repayment_mode || "-"}
                  accent={detailAccent.mode}
                />
                {displayData.repayment_mode === "Full" ? (
                  <RequestDetailField
                    icon={CalendarMonthIcon}
                    label={isDetailedView ? "Disbursement Date" : "Repayment Date"}
                    value={
                      isDetailedView
                        ? formatDisplayDate(displayData.disbursement_date)
                        : formatDisplayDate(displayData.repayment_date)
                    }
                    accent={detailAccent.date}
                  />
                ) : (
                  <>
                    <RequestDetailField
                      icon={EventRepeatIcon}
                      label="No. of Installments"
                      value={
                        displayData.approved_installments ||
                        displayData.no_of_installments ||
                        displayData.number_of_installments ||
                        "-"
                      }
                      helper={
                        displayData.repayment_frequency
                          ? `${displayData.repayment_frequency} frequency`
                          : undefined
                      }
                      accent={detailAccent.installments}
                    />
                    <RequestDetailField
                      icon={CalendarMonthIcon}
                      label={
                        isDetailedView
                          ? "Repayment Start Date"
                          : "Repayment Start Date"
                      }
                      value={formatDisplayDate(displayData.repayment_date)}
                      accent={detailAccent.date}
                    />
                  </>
                )}
                <RequestDetailField
                  icon={DescriptionOutlinedIcon}
                  label="Reason"
                  value={displayData.reason || "-"}
                  accent={detailAccent.reason}
                  multiline
                  isLast={!rejectionReason}
                />
                {rejectionReason ? (
                  <RequestDetailField
                    icon={DescriptionOutlinedIcon}
                    label="Reject Reason"
                    value={rejectionReason}
                    accent={detailAccent.rejectReason}
                    multiline
                    isLast
                  />
                ) : null}
              </Mui.Box>

              {isDetailedView ? (
                <Mui.Box sx={detailCardSx}>
                  <Mui.Typography sx={{ ...detailSectionLabelSx, mb: 1.75 }}>
                    Repayment History
                  </Mui.Typography>
                  <Mui.TableContainer
                    component={Mui.Paper}
                    variant="outlined"
                    sx={{ borderRadius: designRadii.card }}
                  >
                    <Mui.Table size="small">
                      <Mui.TableHead sx={{ backgroundColor: designColors.slate100 }}>
                        <Mui.TableRow>
                          <Mui.TableCell sx={{ fontWeight: 700 }}>Due Date</Mui.TableCell>
                          <Mui.TableCell sx={{ fontWeight: 700 }}>Due</Mui.TableCell>
                          <Mui.TableCell sx={{ fontWeight: 700 }}>Paid</Mui.TableCell>
                          <Mui.TableCell sx={{ fontWeight: 700 }}>Balance</Mui.TableCell>
                          <Mui.TableCell sx={{ fontWeight: 700 }}>Status</Mui.TableCell>
                          {showRepaymentActions ? (
                            <Mui.TableCell sx={{ fontWeight: 700 }} align="center">
                              Action
                            </Mui.TableCell>
                          ) : null}
                        </Mui.TableRow>
                      </Mui.TableHead>
                      <Mui.TableBody>
                        {displayData.repayment_schedule?.length > 0 ? (
                          displayData.repayment_schedule.map((row, index) => (
                            <React.Fragment key={index}>
                              <Mui.TableRow>
                                <Mui.TableCell>{row.due_date || "-"}</Mui.TableCell>
                                <Mui.TableCell>{formatAmount(row.amount_due)}</Mui.TableCell>
                                <Mui.TableCell>{formatAmount(row.amount_paid)}</Mui.TableCell>
                                <Mui.TableCell>{formatAmount(row.balance)}</Mui.TableCell>
                                <Mui.TableCell
                                  sx={{
                                    cursor:
                                      row.skip_status != null ? "pointer" : "default",
                                  }}
                                  onClick={() => {
                                    if (row.skip_status != null) {
                                      setExpandedRow((prev) =>
                                        prev === index ? null : index,
                                      );
                                    }
                                  }}
                                >
                                  <Mui.Tooltip
                                    arrow
                                    title="View skip request details"
                                    disableHoverListener={row.skip_status == null}
                                  >
                                    <Mui.Box
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      {row.status || "-"}
                                      {row.skip_status != null ? (
                                        <InfoOutlinedIcon
                                          sx={{ fontSize: 14, opacity: 0.7 }}
                                        />
                                      ) : null}
                                    </Mui.Box>
                                  </Mui.Tooltip>
                                </Mui.TableCell>
                                {showRepaymentActions ? (
                                  <Mui.TableCell align="center">
                                    <Mui.IconButton
                                      size="small"
                                      disabled={
                                        row.status === "Skipped" ||
                                        row.status === "Paid"
                                      }
                                      onClick={(e) => handleMenuClick(e, row)}
                                    >
                                      <MoreVertIcon fontSize="small" />
                                    </Mui.IconButton>
                                  </Mui.TableCell>
                                ) : null}
                              </Mui.TableRow>
                              {row.skip_status != null ? (
                                <Mui.TableRow>
                                  <Mui.TableCell
                                    colSpan={showRepaymentActions ? 6 : 5}
                                    sx={{ p: 0, bgcolor: designColors.slate100 }}
                                  >
                                    <Mui.Collapse
                                      in={expandedRow === index}
                                      timeout="auto"
                                      unmountOnExit
                                    >
                                      <Mui.Box sx={{ p: 2 }}>
                                        <Mui.Stack spacing={1.25}>
                                          <Mui.Typography
                                            sx={{
                                              fontSize: 13,
                                              fontWeight: 700,
                                              color: designColors.slate800,
                                            }}
                                          >
                                            Skip request details
                                          </Mui.Typography>
                                          <Mui.Typography
                                            sx={approvePanelUi.fieldValue}
                                          >
                                            Status: {row.skip_status || "Pending"}
                                          </Mui.Typography>
                                          <Mui.Typography
                                            sx={approvePanelUi.fieldValue}
                                          >
                                            Requested On: {row.skip_requested_on || "-"}
                                          </Mui.Typography>
                                          <Mui.Typography
                                            sx={approvePanelUi.fieldValue}
                                          >
                                            Reason: {row.skip_reason || "N/A"}
                                          </Mui.Typography>
                                          <Mui.Typography
                                            sx={approvePanelUi.fieldValue}
                                          >
                                            Remark: {row.skip_review_remarks || "N/A"}
                                          </Mui.Typography>
                                          <Mui.Typography
                                            sx={approvePanelUi.fieldValue}
                                          >
                                            {row.skip_status === "Rejected"
                                              ? "Rejected By"
                                              : "Approved By"}
                                            : {row.skip_approved_by || "-"}
                                          </Mui.Typography>
                                          <Mui.Typography
                                            sx={approvePanelUi.fieldValue}
                                          >
                                            {row.skip_status === "Rejected"
                                              ? "Rejected On"
                                              : "Approved On"}
                                            : {row.skip_approved_on || "-"}
                                          </Mui.Typography>
                                        </Mui.Stack>
                                      </Mui.Box>
                                    </Mui.Collapse>
                                  </Mui.TableCell>
                                </Mui.TableRow>
                              ) : null}
                            </React.Fragment>
                          ))
                        ) : (
                          <Mui.TableRow>
                            <Mui.TableCell
                              colSpan={showRepaymentActions ? 6 : 5}
                              align="center"
                              sx={{ py: 3 }}
                            >
                              No schedule available
                            </Mui.TableCell>
                          </Mui.TableRow>
                        )}
                      </Mui.TableBody>
                    </Mui.Table>
                  </Mui.TableContainer>
                  <Mui.Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                  >
                    {selectedRepaymentRow?.status === "Pending" ? (
                      <Mui.MenuItem onClick={openSkipModal}>Skip Request</Mui.MenuItem>
                    ) : null}
                    {selectedRepaymentRow?.status === "Skip-Requested" ? (
                      <Mui.MenuItem onClick={openReviewModal}>Review Skip</Mui.MenuItem>
                    ) : null}
                  </Mui.Menu>
                </Mui.Box>
              ) : null}

              <RequestStatusTimeline
                data={statusFlowData}
                variant="stepper"
                forceCompleted={isAdvanceCompleted}
                hidePendingStep
              />
              <ApprovalEditAuditTrail data={displayData} />
            </>
          )}
        </Mui.Box>
      </GlobalSidePanel>
      {rejectDialog}
      {cancelDialog}
      {skipDialog}
      {reviewDialog}
      <AdvanceSalaryPanel
        open={editPanelOpen}
        onClose={handleEditClose}
        reCallApi={setReCallApi}
        mode="edit"
        initialData={displayData}
        checkAccess={hasApproverAccess}
        requestUserId={displayData?.user_id || advanceSalary?.user_id}
      />
    </>
  );
};
